import React, { HTMLAttributeAnchorTarget, HTMLAttributes } from 'react';
import csx from 'classnames';
import s from '#components/controls/buttons/button/Button.module.scss';
import useTranslation from 'next-translate/useTranslation';

interface Props extends HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
  text: JSX.Element | string | undefined;
  href?: string;
  type?: 'button' | 'submit' | 'reset';
  size?: 'small' | 'medium' | 'large';
  transparent?: boolean;
  isSecondary?: boolean;
  isOutline?: boolean;
  isGhost?: boolean;
  isActive?: boolean;
  price?: string | number;
  extraClass?: string;
  classes?: string;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent) => void;
  target?: HTMLAttributeAnchorTarget;
  addition?: string;
  tag?: string;
  startIcon?: JSX.Element;
  endIcon?: JSX.Element;
  fontWeight?: 'bold' | 'normal';
  disablePadding?: boolean;
  selected?: boolean;
  dataTestId?: string;
  fullWidth?: boolean;
  changeColorOnHover?: boolean;
}

const Button = ({
  href,
  startIcon,
  endIcon,
  type = 'button',
  isSecondary,
  extraClass,
  isOutline,
  isGhost,
  isActive,
  text,
  onClick,
  disabled,
  target = '_blank',
  price,
  addition,
  tag,
  classes,
  transparent,
  fontWeight = 'bold',
  disablePadding = false,
  selected,
  dataTestId,
  fullWidth,
  changeColorOnHover,
  ...otherProps
}: Props) => {
  const blockClass = csx(s.host, extraClass, classes, {
    [s.isPrimary]: !isSecondary && !isGhost,
    [s.isSecondary]: !!isSecondary,
    [s.isOutline]: !!isOutline,
    [s.isGhost]: !!isGhost,
    [s.isActive]: !!isActive,
    [s.isTransparent]: !!transparent,
    [s.hasStartIcon]: !!startIcon,
    [s.hasEndIcon]: !!endIcon,
    [s.bold]: fontWeight === 'bold',
    [s.isLink]: !!href,
    [s.fullWidth]: !!fullWidth,
    [s.changeColorOnHover]: !!changeColorOnHover,
  });

  if (href) {
    return (
      <a
        href={href}
        data-testid={dataTestId}
        target={target}
        className={blockClass}
        rel="noreferrer"
        {...otherProps}
      >
        <ButtonInner
          text={text}
          price={price}
          addition={addition}
          tag={tag}
          startIcon={startIcon}
          endIcon={endIcon}
          selected={selected}
          disablePadding={disablePadding}
          fullWidth={fullWidth}
        />
      </a>
    );
  }

  return (
    <button
      className={blockClass}
      data-testid={dataTestId}
      type={type}
      onClick={onClick}
      disabled={disabled}
      {...otherProps}
    >
      <ButtonInner
        text={text}
        price={price}
        addition={addition}
        tag={tag}
        startIcon={startIcon}
        endIcon={endIcon}
        disablePadding={disablePadding}
        selected={selected}
        fullWidth={fullWidth}
      />
    </button>
  );
};

type InnerProps = Pick<
  Props,
  | 'text'
  | 'price'
  | 'addition'
  | 'tag'
  | 'startIcon'
  | 'endIcon'
  | 'disablePadding'
  | 'selected'
  | 'fullWidth'
>;

const ButtonInner = ({
  text,
  price,
  addition,
  tag,
  startIcon,
  endIcon,
  disablePadding = false,
  selected,
  fullWidth,
}: InnerProps) => {
  let newPrice = '';

  const { t, lang: locale } = useTranslation();

  if (price) {
    const priceSettings = { style: 'currency', currency: 'EUR' };
    newPrice = Number(price).toLocaleString(locale, priceSettings);
  }

  return (
    <span
      className={`${s.inner} ${!disablePadding ? s.padding : ''} ${
        selected ? s.selected : ''
      }
      ${fullWidth ? s.alignLeft : ''}`}
    >
      {!!startIcon && <span className={s.startIcon}>{startIcon}</span>}
      <span>
        {text}
        {newPrice !== '' && (
          <>
            &nbsp;{newPrice}
            <dfn className={s.notion} title={t('pricesIncludingTax')}>
              1
            </dfn>
          </>
        )}
      </span>
      {!!endIcon && <span className={s.endIcon}>{endIcon}</span>}
      {addition && <span className={s.addition}>{addition}</span>}
      {tag && <span className={s.additionTag}>{tag}</span>}
    </span>
  );
};

export default Button;
